// ============================================================================
// MY TASKS PANEL
// Tasks assigned to current user (owner or team). Default filter: today.
// Optional propertyId: filter to one property (e.g. single-property view).
// When canExecuteTasksForTeam is false, team-assigned tasks show a lock message.
// ============================================================================

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { todayYmd } from "@/lib/turnoverWindow";
import { useMyTasks, type MyTaskItem } from "@/hooks/useMyTasks";
import { formatScheduledDate } from "@/lib/dateUtils";
import { Play, Lock } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

type DateFilter = "today" | "week" | "all";

export interface MyTasksPanelProps {
  className?: string;
  /** When set, only tasks for this property are shown (e.g. single-property page). */
  propertyId?: string;
  /** Override heading (e.g. "My tasks for this property"). */
  title?: string;
  /** For team members: when false, tasks with assigned_type === 'team' show lock message instead of Start. */
  canExecuteTasksForTeam?: boolean;
}

export function MyTasksPanel({ className, propertyId, title, canExecuteTasksForTeam = true }: MyTasksPanelProps) {
  const { tDynamic } = useTranslation();
  const navigate = useNavigate();
  // Default to week so upcoming + overdue assigned work is visible (not only exact today).
  const [dateFilter, setDateFilter] = useState<DateFilter>("week");
  const { data: tasks = [], isLoading } = useMyTasks(dateFilter, propertyId);

  const handleStartTask = (t: MyTaskItem) => {
    if (t.assigned_type === "cleaner") {
      navigate(`/clean/${t.property_id}?taskId=${t.id}`);
    } else {
      navigate(`/tasks/run/${t.property_id}?taskId=${t.id}`);
    }
  };

  const today = todayYmd();
  const weekEnd = new Date();
  weekEnd.setDate(weekEnd.getDate() + 7);
  const weekEndYmd = todayYmd(weekEnd);
  const groups: { key: string; label: string; items: MyTaskItem[] }[] = [
    { key: "overdue", label: tDynamic("Overdue"), items: tasks.filter((t) => t.scheduled_date.slice(0, 10) < today) },
    { key: "today", label: tDynamic("Today"), items: tasks.filter((t) => t.scheduled_date.slice(0, 10) === today) },
    {
      key: "week",
      label: tDynamic("This week"),
      items: tasks.filter((t) => {
        const d = t.scheduled_date.slice(0, 10);
        return d > today && d <= weekEndYmd;
      }),
    },
    { key: "later", label: tDynamic("Later"), items: tasks.filter((t) => t.scheduled_date.slice(0, 10) > weekEndYmd) },
  ];
  const filters: { v: DateFilter; label: string }[] = [
    { v: "today", label: tDynamic("Today") },
    { v: "week", label: tDynamic("This week") },
    { v: "all", label: tDynamic("All") },
  ];

  const renderTask = (t: MyTaskItem, overdue: boolean, isToday: boolean) => {
    const isTeamTaskNoPermission = t.assigned_type === "team" && canExecuteTasksForTeam === false;
    const d = new Date(`${t.scheduled_date.slice(0, 10)}T12:00:00`);
    const dow = d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase();
    return (
      <div key={t.id} className="flex items-center gap-3.5 border-b-[1.5px] border-[#f1f4f6] px-4 py-3.5 last:border-b-0 max-sm:flex-wrap">
        <div
          className={cn(
            "flex h-[46px] w-[46px] shrink-0 flex-col items-center justify-center rounded-[14px] leading-none",
            isToday ? "bg-[#15776d] text-white" : overdue ? "bg-[#fdf0d5] text-[#92560a]" : "bg-[#f1f4f6] text-[#334155]",
          )}
        >
          <span className="text-[10px] font-bold tracking-[0.5px]">{dow}</span>
          <span className="mt-0.5 text-[18px] font-bold">{d.getDate()}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-[16px] font-bold tracking-[-0.2px] text-[#0f1720]">
              {t.is_test ? tDynamic("Test clean") : t.checklist_name || tDynamic("Turnover")}
            </span>
            {overdue ? (
              <span className="rounded-full bg-[#fdf0d5] px-2.5 py-0.5 text-[11.5px] font-bold text-[#92560a]">
                {tDynamic("Overdue")}
              </span>
            ) : null}
          </div>
          <div className="truncate text-[13px] font-medium text-[#64748b]">
            {t.property_name ?? tDynamic("Property")} · {formatScheduledDate(t.scheduled_date, "EEE, MMM d")}
          </div>
        </div>
        {isTeamTaskNoPermission ? (
          <p className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-[#64748b]">
            <Lock className="h-3.5 w-3.5" />
            {tDynamic("Owner must enable Can do tasks.")}
          </p>
        ) : (
          <button
            type="button"
            onClick={() => handleStartTask(t)}
            className={cn(
              "flex h-10 shrink-0 items-center gap-2 rounded-xl px-4 text-[14px] font-bold",
              isToday || overdue ? "bg-[#24a89a] text-white hover:bg-[#1f958a]" : "bg-[#f1f6f5] text-[#15776d] hover:bg-[#e2eeed]",
            )}
          >
            <Play className="h-3.5 w-3.5 fill-current" />
            {tDynamic("Start task")}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex rounded-[14px] bg-white p-1" role="tablist" aria-label={displayTitle(title, propertyId, tDynamic)}>
          {filters.map((f) => (
            <button
              key={f.v}
              type="button"
              role="tab"
              aria-selected={dateFilter === f.v}
              onClick={() => setDateFilter(f.v)}
              className={cn(
                "rounded-[10px] px-[22px] py-[9px] text-[14px] font-bold transition-colors",
                dateFilter === f.v ? "bg-[#15776d] text-white" : "text-[#64748b]",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        {!isLoading && tasks.length > 0 ? (
          <div className="text-[13px] font-medium text-[#94a3b8]">
            {tasks.length} {tasks.length === 1 ? tDynamic("task assigned to you") : tDynamic("tasks assigned to you")}
          </div>
        ) : null}
      </div>

      {isLoading ? (
        <div className="rounded-[24px] bg-white py-8 text-center text-[#64748b]">{tDynamic("Loading...")}</div>
      ) : tasks.length === 0 ? (
        <div className="rounded-[24px] bg-white px-6 py-10 text-center text-[15px] font-medium text-[#64748b]">
          {dateFilter === "today"
            ? tDynamic("No tasks assigned to you today or overdue.")
            : tDynamic("No tasks assigned to you in this range.")}
        </div>
      ) : (
        groups
          .filter((g) => g.items.length > 0)
          .map((g) => (
            <div key={g.key} className="flex flex-col gap-3">
              <div
                className={cn(
                  "text-[19px] font-medium tracking-[-0.2px]",
                  g.key === "overdue" ? "text-[#92560a]" : "text-[#0f1720]",
                )}
              >
                {g.label}
              </div>
              <div className="overflow-hidden rounded-[24px] bg-white">
                {g.items.map((t) => renderTask(t, g.key === "overdue", g.key === "today"))}
              </div>
            </div>
          ))
      )}
    </div>
  );
}

function displayTitle(title: string | undefined, propertyId: string | undefined, tDynamic: (s: string) => string) {
  return title ?? (propertyId ? tDynamic("My tasks for this property") : tDynamic("My tasks"));
}
